import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useNode, useEditor } from '@splashthat/splash-cms-engine';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';
import {
    BRAND_LIBRARY_LIGHT_TEXT_COLOR,
    BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
    BRAND_LIBRARY_BUTTON_HOVER_COLOR,
    BRAND_LIBRARY_BUTTON_TEXT,
} from 'common/constants/brandLibrary';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { softSanitizeUrl } from 'common/components/TouchpointEditor/utils';
import useDynamicTags from 'common/components/TouchpointEditor/hooks/useDynamicTags';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';

import { DefaultButtonComponent } from './Button.styles';

const Button = React.forwardRef(
    (
        {
            styles: {
                backgroundColor,
                backgroundColorHover,
                fontColor,
                fontColorHover,
                fontSize,
                fontFamily,
                borderColor,
                borderColorHover,
                borderWidth,
                borderRadius,
            },
            meta: { content, href, visible, target },
            onClick,
            selected,
            hovered,
            to,
            dataQa,
            replace,
        },
        ref,
    ) => {
        const node = useNode();
        const editor = useEditor((state) => ({ editing: state.editing }));
        const {
            theme: { activeFonts, colors },
        } = useTouchpointData();
        const link = useDynamicTags(href);
        const customCssClassName = getCustomCssClassName(node);

        const fontStyles = useMemo(
            () => ({
                $fontFamily: activeFonts[fontFamily],
                $fontColor: colors[fontColor],
                $fontColorHover: colors[fontColorHover],
            }),
            [fontFamily, fontColor, fontColorHover],
        );

        const backgroundStyles = useMemo(
            () => ({
                $backgroundColor: colors[backgroundColor],
                $backgroundColorHover: colors[backgroundColorHover],
            }),
            [backgroundColor, backgroundColorHover],
        );

        const borderStyles = useMemo(
            () => ({
                $borderColor: colors[borderColor],
                $borderColorHover: colors[borderColorHover],
                $borderWidth: borderWidth,
                $borderRadius: borderRadius,
            }),
            [borderColor, borderColorHover, borderWidth, borderRadius],
        );

        const setContent = useCallback(
            (text) =>
                node.actions.setProp((editedNode) => {
                    editedNode.meta.content = text; // eslint-disable-line no-param-reassign
                    return editedNode;
                }),
            [],
        );

        const getAsAccordingToProperties = () => {
            if (link) {
                return 'a';
            }

            if (to) {
                return Link;
            }

            return 'button';
        };

        return visible ? (
            <DefaultButtonComponent
                $fontSize={fontSize}
                hovered={hovered}
                href={softSanitizeUrl(link) || null}
                as={getAsAccordingToProperties()}
                to={to}
                target={target}
                rel="noopener noreferrer"
                onClick={node.actions.onClick(onClick)}
                onMouseLeave={node.actions.onMouseLeave()}
                onMouseMove={node.actions.onMouseMove()}
                ref={ref}
                selected={selected}
                replace={replace}
                {...backgroundStyles}
                {...borderStyles}
                {...fontStyles}
                data-qa={dataQa}
                className={customCssClassName}
            >
                <TextEditable
                    setContent={setContent}
                    disabled={!editor.editing || (editor.editing && !selected && !hovered)}
                >
                    {content}
                </TextEditable>
            </DefaultButtonComponent>
        ) : null;
    },
);

Button.splash = {
    related: {
        settings: React.lazy(() => import('./Button.tools')),
    },
    defaultProps: {
        styles: {
            backgroundColor: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
            backgroundColorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
            fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontColorHover: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontSize: 16,
            fontFamily: BRAND_LIBRARY_BUTTON_TEXT,
            borderColor: BRAND_LIBRARY_BUTTON_DEFAULT_COLOR,
            borderColorHover: BRAND_LIBRARY_BUTTON_HOVER_COLOR,
            borderWidth: {
                top: 1,
                bottom: 1,
                left: 1,
                right: 1,
            },
            borderRadius: 4,
        },
        meta: {
            content: 'Register',
            href: '',
            visible: true,
            target: '_self',
        },
    },
};

Button.propTypes = {
    meta: PropTypes.shape({
        content: PropTypes.string.isRequired,
        href: PropTypes.string,
        visible: PropTypes.bool,
    }).isRequired,
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    styles: PropTypes.shape({
        backgroundColor: PropTypes.number,
        backgroundColorHover: PropTypes.number,
        fontColor: PropTypes.number,
        fontColorHover: PropTypes.number,
        fontSize: PropTypes.number,
        fontFamily: PropTypes.number,
        borderColor: PropTypes.number,
        borderColorHover: PropTypes.number,
        borderStyle: PropTypes.string,
        borderWidth: PropTypes.object,
        borderRadius: PropTypes.number,
    }).isRequired,
    target: PropTypes.string,
    to: PropTypes.string,
    dataQa: PropTypes.string,
    onClick: PropTypes.func,
    replace: PropTypes.bool,
};

Button.defaultProps = {
    target: null,
    to: null,
    dataQa: undefined,
    onClick: undefined,
    replace: false,
};

export default Button;
